export interface WhatsappWebsocketUrlParams {
    applicantId: string;
    companyId: number;
    ticket: string;
}

export interface WhatsappTemplateParameterDefinition {
    id: string;
    componentType: "header" | "body";
    order: number;
    label: string;
    sourceKey?: string;
    sourceType?: "SYSTEM" | "MANUAL";
}

export interface WhatsappTemplateAnalysis {
    supported: boolean;
    reason: string;
    parameterDefinitions: WhatsappTemplateParameterDefinition[];
}

export interface WhatsappTemplateSendContext {
    requiresJob: boolean;
    label: string;
}

export interface WhatsappProfileStatusLabels {
    statusAccepted: string;
    statusDeleted: string;
    statusDelivered: string;
    statusFailed: string;
    statusRead: string;
    statusReceived: string;
    statusSent: string;
    statusUnknown: string;
}

export const getWhatsappProfileStatusLabel = (
    status: string | undefined,
    labels: WhatsappProfileStatusLabels
) => {
    switch (String(status || "").toUpperCase()) {
        case "ACCEPTED":
            return labels.statusAccepted;
        case "DELETED":
            return labels.statusDeleted;
        case "DELIVERED":
            return labels.statusDelivered;
        case "FAILED":
            return labels.statusFailed;
        case "READ":
            return labels.statusRead;
        case "RECEIVED":
            return labels.statusReceived;
        case "SENT":
            return labels.statusSent;
        default:
            return labels.statusUnknown;
    }
};

const WHATSAPP_GLOBAL_TEMPLATE_CONTEXTS: Record<
    string,
    WhatsappTemplateSendContext
> = {
    candidate_application_received: {
        requiresJob: true,
        label: "Processo seletivo"
    },
    candidate_process_update: {
        requiresJob: true,
        label: "Processo seletivo"
    },
    candidate_process_closed: {
        requiresJob: true,
        label: "Processo seletivo"
    },
    candidate_offer_next_steps: {
        requiresJob: true,
        label: "Processo seletivo"
    },
    candidate_new_opportunity: {
        requiresJob: true,
        label: "Nova oportunidade"
    },
    candidate_interview_scheduled: {
        requiresJob: true,
        label: "Entrevista"
    },
    candidate_interview_reminder: {
        requiresJob: true,
        label: "Entrevista"
    },
    candidate_assessment_request: {
        requiresJob: true,
        label: "Teste ou avaliacao"
    }
};

const PLACEHOLDER_REGEX = /{{\s*(\d+)\s*}}/g;

const getPlaceholderIndexes = (text?: string) => {
    const values = new Set<number>();
    const source = text || "";
    let match = PLACEHOLDER_REGEX.exec(source);

    while (match) {
        values.add(Number(match[1]));
        match = PLACEHOLDER_REGEX.exec(source);
    }

    return Array.from(values).sort((itemA, itemB) => itemA - itemB);
};

export const buildWhatsappWebsocketUrl = (
    apiBaseUrl: string,
    params: WhatsappWebsocketUrlParams
) => {
    const trimmedBaseUrl = apiBaseUrl.replace(/\/$/, "");
    const websocketBaseUrl = trimmedBaseUrl.replace(/^http/i, "ws");
    const searchParams = new URLSearchParams({
        ticket: params.ticket,
        companyId: String(params.companyId),
        applicantId: params.applicantId
    });

    return `${websocketBaseUrl}/whatsapp/ws?${searchParams.toString()}`;
};

export const getApiErrorMessage = (error: any, fallbackMessage: string) => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        fallbackMessage
    );
};

export const getWhatsappTemplateName = (template?: Compleo.IObject | null) => {
    return String(template?.templateName || template?.name || "");
};

export const getWhatsappTemplateSendContext = (
    template?: Compleo.IObject | null
): WhatsappTemplateSendContext => {
    if (!template) {
        return {
            requiresJob: false,
            label: ""
        };
    }

    const identity = String(
        template.sourceGlobalTemplateKey || getWhatsappTemplateName(template)
    );
    const mapped = WHATSAPP_GLOBAL_TEMPLATE_CONTEXTS[identity];
    if (mapped) {
        return mapped;
    }

    const messageType = String(template.messageType || "");
    if (
        [
            "communicationApplicantJob",
            "recruiting_process",
            "interview",
            "assessment",
            "new_opportunity"
        ].includes(messageType)
    ) {
        return {
            requiresJob: true,
            label: ""
        };
    }

    const definitions = (template.parameterDefinitions ||
        []) as Compleo.IObject[];
    return {
        requiresJob: definitions.some((definition) =>
            String(definition.sourceKey || "").match(
                /^(job|schedule|assessment|manual\.interview|manual\.assessment|manual\.deadline)/
            )
        ),
        label: ""
    };
};

export const canUseApplicantWhatsappPanel = (params: {
    hasJobAccess: boolean;
    whatsappEnabled?: boolean;
}) => {
    return params.whatsappEnabled === true && params.hasJobAccess === true;
};

export const canAccessApplicantWhatsappBaseConversation = (
    conversationData?: Compleo.IObject | null
) => conversationData?.canAccessBaseConversation === true;

export const canUseApplicantWhatsappBaseActions = (params: {
    canMutate: boolean;
    conversationData?: Compleo.IObject | null;
}) =>
    params.canMutate &&
    canAccessApplicantWhatsappBaseConversation(params.conversationData);

export const analyseWhatsappTemplate = (
    template: Compleo.IObject | null | undefined
): WhatsappTemplateAnalysis => {
    if (!template) {
        return {
            supported: false,
            reason: "Template not found",
            parameterDefinitions: []
        };
    }

    const definitions: WhatsappTemplateParameterDefinition[] = [];

    for (const rawComponent of template.components || []) {
        const componentType = String(rawComponent?.type || "").toUpperCase();

        if (componentType === "BUTTONS") {
            const hasDynamicButton = (rawComponent.buttons || []).some(
                (button: Compleo.IObject) =>
                    getPlaceholderIndexes(button?.url || button?.text || "")
                        .length > 0
            );

            if (hasDynamicButton) {
                return {
                    supported: false,
                    reason: "Dynamic button parameters are not supported yet",
                    parameterDefinitions: []
                };
            }

            continue;
        }

        if (!["HEADER", "BODY", "FOOTER"].includes(componentType)) {
            return {
                supported: false,
                reason: `Unsupported template component type: ${componentType || "unknown"}`,
                parameterDefinitions: []
            };
        }

        if (componentType === "FOOTER") {
            continue;
        }

        if (
            componentType === "HEADER" &&
            rawComponent?.format &&
            String(rawComponent.format).toUpperCase() !== "TEXT"
        ) {
            return {
                supported: false,
                reason: "Only text headers are supported in the first version",
                parameterDefinitions: []
            };
        }

        const indexes = getPlaceholderIndexes(rawComponent?.text);

        for (const index of indexes) {
            const currentType = componentType === "HEADER" ? "header" : "body";
            const storedDefinition = template.parameterDefinitions?.find(
                (definition: Compleo.IObject) =>
                    Number(definition.index) === index &&
                    (!definition.componentType ||
                        definition.componentType === currentType)
            );
            const componentDefinition = rawComponent.parameterDefinitions?.find(
                (definition: Compleo.IObject) =>
                    Number(definition.index) === index &&
                    (!definition.componentType ||
                        definition.componentType === currentType)
            );
            const definition: WhatsappTemplateParameterDefinition = {
                id: `${currentType}-${index}`,
                componentType: currentType,
                order: index,
                label:
                    componentDefinition?.label ||
                    storedDefinition?.label ||
                    `${componentType} parameter ${index}`
            };

            if (storedDefinition?.sourceKey) {
                definition.sourceKey = storedDefinition.sourceKey;
            }
            if (storedDefinition?.sourceType) {
                definition.sourceType = storedDefinition.sourceType;
            }

            definitions.push(definition);
        }
    }

    return {
        supported: true,
        reason: "",
        parameterDefinitions: definitions
    };
};

export const buildWhatsappTemplateComponents = (params: {
    parameterDefinitions: WhatsappTemplateParameterDefinition[];
    values: Record<string, string>;
}) => {
    const componentsByType: Record<string, { type: "text"; text: string }[]> =
        {};

    for (const definition of params.parameterDefinitions) {
        const currentValue = params.values[definition.id];

        if (!currentValue) {
            continue;
        }

        if (!componentsByType[definition.componentType]) {
            componentsByType[definition.componentType] = [];
        }

        componentsByType[definition.componentType].push({
            type: "text",
            text: currentValue
        });
    }

    return Object.entries(componentsByType).map(([componentType, values]) => ({
        type: componentType,
        parameters: values
    }));
};
